/**
 * useApplicantDashboard — consolidates every data fetch the
 * refreshed `ApplicantDashboard` needs into a single
 * `AsyncResource<ApplicantDashboardData>`.
 *
 * The hook replaces the constellation of `useEffect` calls that the
 * legacy dashboard used (one for applicant + year profiles, one for
 * documents, one for service hours, one for Instagram link status,
 * one for upcoming events). Pulling them behind one hook produces a
 * single loading boundary, a single error boundary, and a stable
 * data shape that is easy to test.
 *
 * Real GraphQL is preserved verbatim from the legacy dashboard — no
 * new backend calls, no new fields. The only structural addition is
 * a derived `profileCompletion` field that summarises the form
 * fragments the applicant has populated so far (also computed in the
 * legacy `ApprovedCard`, just lifted here so the new dashboard can
 * surface it as a KPI tile rather than buried inside a status card).
 *
 * Spec: meridian-redesign — PR (m.7) Dashboard refresh
 * Requirements: 7.1, 7.2, 7.3, 7.4, 7.5, 7.6, 7.7, 14.1, 18.5
 */

import { useCallback, useEffect, useState } from 'react';
import { generateClient } from 'aws-amplify/api';
import type { GraphQLResult } from 'aws-amplify/api';
import type { SubmissionStatus } from '@/types';
import { fetchUpcomingEventsFromApi, limitUpcomingEvents, type CalendarEvent } from '@/data/calendarEvents';
import { resolveCallerPersonId } from '@/lib/application-form/client/callerIdentity';
import { fetchAllDocumentReferences } from '@/lib/application-form/identity/applicantIdentity';
import { useActiveSeason } from '@/hooks/useActiveSeason';
import type { AsyncResource } from './asyncResource';

const client = generateClient();

export interface ApplicantDashboardYearProfile {
  applicantId: string;
  competitionYear: number;
  applicantType: string;
  submissionStatus: SubmissionStatus;
  profileLocked: boolean;
  placementQuestionsLocked: boolean;
  requestedTitle?: string | null;
  approvedTitle?: string | null;
  titleApproved?: boolean | null;
  directorNotes?: string | null;
  phase1Data: {
    age?: number | null;
    dateOfBirth?: string | null;
    birthplace?: string | null;
    usCitizen?: boolean | null;
    eyeColor?: string | null;
    hairColor?: string | null;
    occupation?: string | null;
    currentCommunityInvolvement?: string | null;
    hobbies?: string | null;
    talents?: string | null;
    spouseName?: string | null;
    marriageDate?: string | null;
    generalBackground?: string | null;
  } | null;
  stageFormData?: {
    stageName?: string | null;
    fitnessRoutine?: string | null;
    mostImportantLearning?: string | null;
  } | null;
  interviewInfoData?: {
    nameAndTitle?: string | null;
    pageantAccomplishmentGoals?: string | null;
  } | null;
  placementQuestionsData?: {
    top15Response?: string | null;
    interviewJudgesQuestion?: string | null;
  } | null;
}

export interface ApplicantDashboardApplicant {
  id: string;
  email: string;
  fullName: string;
  cognitoSub: string;
  yearProfiles: ApplicantDashboardYearProfile[];
}

/**
 * Derived summary of the applicant's progress through the form
 * fragments expected for their applicant type. The five fragments
 * (phase1, stage, interview, placement, costume submission) match
 * the legacy `ApprovedCard` checklist; the legacy code only
 * surfaced `done / total` for approved applicants — the refreshed
 * dashboard surfaces it as a KPI tile across every state.
 */
export interface ProfileCompletion {
  /** How many of the expected fragments have at least one populated field. */
  completed: number;
  /** Total fragments expected for this applicant. */
  total: number;
  /** `completed / total` rounded to nearest percent. */
  percent: number;
}

export interface ApplicantDashboardData {
  /** The full applicant record + year profiles. `null` if no record exists. */
  applicant: ApplicantDashboardApplicant | null;
  /** The currently-active year profile, or `null` if no record. */
  currentProfile: ApplicantDashboardYearProfile | null;
  /** Number of documents uploaded for this competition year. */
  docCount: number;
  /** Set of `documentType` strings already uploaded. */
  uploadedDocTypes: Set<string>;
  /** Sum of `hoursContributed` across all volunteer activities. */
  serviceHours: number;
  /** Whether the applicant has an Instagram account linked. */
  instagramLinked: boolean;
  /** Up to three upcoming calendar events. */
  upcomingEvents: CalendarEvent[];
  /** Derived per-fragment progress summary. */
  profileCompletion: ProfileCompletion;
  /** The competition year being viewed. */
  competitionYear: number;
}

export interface UseApplicantDashboardArgs {
  /** Cognito sub of the authenticated applicant; `null` if signed out. */
  cognitoSub: string | null | undefined;
  /** The applicant's email — used as the listApplicants filter key. */
  email: string | null | undefined;
}

/**
 * Compute `done / total` over the form fragments expected for the
 * applicant's type. Mirrors the logic in the legacy `ApprovedCard`
 * (which only ran for approved applicants); pulling it into the
 * hook means every dashboard state can render a completion KPI.
 */
export function computeProfileCompletion(
  profile: ApplicantDashboardYearProfile | null,
): ProfileCompletion {
  if (!profile) return { completed: 0, total: 0, percent: 0 };

  const phase1Started = !!(
    profile.phase1Data?.occupation?.trim() ||
    profile.phase1Data?.dateOfBirth?.trim() ||
    profile.phase1Data?.generalBackground?.trim()
  );
  const stageStarted = !!(
    profile.stageFormData &&
    (profile.stageFormData.stageName?.trim() ||
      profile.stageFormData.fitnessRoutine?.trim() ||
      profile.stageFormData.mostImportantLearning?.trim())
  );
  const interviewStarted = !!(
    profile.interviewInfoData &&
    (profile.interviewInfoData.nameAndTitle?.trim() ||
      profile.interviewInfoData.pageantAccomplishmentGoals?.trim())
  );
  const placementStarted =
    profile.placementQuestionsLocked ||
    !!(
      profile.placementQuestionsData &&
      (profile.placementQuestionsData.top15Response?.trim() ||
        profile.placementQuestionsData.interviewJudgesQuestion?.trim())
    );

  let costumeSubmitted = false;
  try {
    if (profile.phase1Data?.generalBackground) {
      const bg = JSON.parse(profile.phase1Data.generalBackground);
      costumeSubmitted = !!bg?.costumeSubmitted;
    }
  } catch {
    // Malformed generalBackground JSON — treat as not submitted.
  }

  const total = 5;
  const fragments = [
    phase1Started,
    stageStarted,
    interviewStarted,
    placementStarted,
    costumeSubmitted,
  ];
  const completed = fragments.filter(Boolean).length;
  return {
    completed,
    total,
    percent: Math.round((completed / total) * 100),
  };
}

export function useApplicantDashboard({
  cognitoSub,
  email,
}: UseApplicantDashboardArgs): AsyncResource<ApplicantDashboardData> {
  const [data, setData] = useState<ApplicantDashboardData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [refetchTick, setRefetchTick] = useState(0);

  // Season SSOT: resolve the operative competition year through the
  // active-season seam rather than a static COMPETITION_YEAR literal.
  const { operativeYear } = useActiveSeason();

  const refetch = useCallback(() => {
    setRefetchTick((t) => t + 1);
  }, []);

  useEffect(() => {
    if (!cognitoSub || !email) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        // 1. Look up the applicant record by email. Mirrors the
        //    legacy fetch which paginated `listApplicants(limit:200)`
        //    rather than calling `getApplicantByCognitoSub` directly
        //    (the latter has unreliable userPool auth).
        const listResult = (await client.graphql({
          query: `query { listApplicants(limit: 200) { items { id email fullName cognitoSub } } }`,
          authMode: 'userPool',
        })) as GraphQLResult<{
          listApplicants: { items: ApplicantDashboardApplicant[] };
        }>;
        const all = listResult.data?.listApplicants?.items ?? [];
        const match = all.find((a) => a.email === email) ?? null;

        let applicant: ApplicantDashboardApplicant | null = null;
        if (match) {
          // 2. Year profiles for the matched applicant.
          let yearProfiles: ApplicantDashboardYearProfile[] = [];
          try {
            const yp = (await client.graphql({
              query: `query($applicantId: ID!) {
                listApplicantYearProfiles(applicantId: $applicantId) {
                  applicantId competitionYear applicantType submissionStatus
                  profileLocked placementQuestionsLocked
                  requestedTitle approvedTitle titleApproved directorNotes
                  phase1Data {
                    age dateOfBirth birthplace usCitizen eyeColor hairColor
                    occupation currentCommunityInvolvement hobbies talents
                    spouseName marriageDate generalBackground
                  }
                  stageFormData { stageName fitnessRoutine mostImportantLearning }
                  interviewInfoData { nameAndTitle pageantAccomplishmentGoals }
                  placementQuestionsData { top15Response interviewJudgesQuestion }
                }
              }`,
              variables: { applicantId: match.id },
              authMode: 'userPool',
            })) as GraphQLResult<{
              listApplicantYearProfiles: ApplicantDashboardYearProfile[];
            }>;
            yearProfiles = yp.data?.listApplicantYearProfiles ?? [];
          } catch {
            yearProfiles = [];
          }
          applicant = { ...match, yearProfiles };
        }

        const currentProfile =
          applicant?.yearProfiles?.find(
            (p) => p.competitionYear === operativeYear,
          ) ?? null;

        // 3. Documents uploaded for this competition year.
        let docCount = 0;
        let uploadedDocTypes = new Set<string>();
        if (applicant) {
          try {
            // Reconciled Dual_Read: a contestant's DocumentReferences may be keyed
            // under EITHER the legacy Applicant.id (legacy uploader) OR their
            // personId (the new /apply uploader). Read both id spaces and union
            // by document id so the count is accurate for either flow and never
            // double-counts a shared row (applicant-id-ssot-reconciliation Req 2).
            const personId = await resolveCallerPersonId().catch(() => null);
            const listByApplicantId = async (id: string, yr: number) => {
              const docs = (await client.graphql({
                query: `query($aid: ID!, $yr: Int!) {
                  listDocumentReferences(applicantId: $aid, competitionYear: $yr) {
                    id documentType
                  }
                }`,
                variables: { aid: id, yr },
                authMode: 'userPool',
              })) as GraphQLResult<{
                listDocumentReferences: Array<{ id: string; documentType: string }>;
              }>;
              return docs.data?.listDocumentReferences ?? [];
            };
            const merged = await fetchAllDocumentReferences(
              { personId, legacyApplicantId: applicant.id },
              operativeYear,
              { listByApplicantId },
            );
            docCount = merged.length;
            uploadedDocTypes = new Set(merged.map((d) => d.documentType));
          } catch {
            // Soft fail — render dashboard with 0 docs rather than blocking.
          }
        }

        // 4. Service hours (sum across all volunteer activities).
        let serviceHours = 0;
        if (applicant) {
          try {
            const vol = (await client.graphql({
              query: `query($aid: ID!) {
                listVolunteerActivities(applicantId: $aid) { hoursContributed }
              }`,
              variables: { aid: applicant.id },
              authMode: 'userPool',
            })) as GraphQLResult<{
              listVolunteerActivities: Array<{ hoursContributed: number }>;
            }>;
            const items = vol.data?.listVolunteerActivities ?? [];
            serviceHours = items.reduce(
              (sum, a) => sum + (a.hoursContributed ?? 0),
              0,
            );
          } catch {
            // Soft fail.
          }
        }

        // 5. Instagram link status.
        let instagramLinked = false;
        try {
          const ig = (await client.graphql({
            query: `query($cognitoSub: String!) {
              getInstagramTokenInfo(cognitoSub: $cognitoSub) { linkStatus }
            }`,
            variables: { cognitoSub },
            authMode: 'userPool',
          })) as GraphQLResult<{
            getInstagramTokenInfo: { linkStatus: string } | null;
          }>;
          instagramLinked =
            ig.data?.getInstagramTokenInfo?.linkStatus === 'connected';
        } catch {
          // Soft fail — treat as unlinked.
        }

        // 6. Upcoming events.
        const events = await fetchUpcomingEventsFromApi(operativeYear);
        const upcomingEvents = limitUpcomingEvents(events, 3);

        if (cancelled) return;

        setData({
          applicant,
          currentProfile,
          docCount,
          uploadedDocTypes,
          serviceHours,
          instagramLinked,
          upcomingEvents,
          profileCompletion: computeProfileCompletion(currentProfile),
          competitionYear: operativeYear,
        });
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [cognitoSub, email, refetchTick, operativeYear]);

  return { data, loading, error, refetch };
}
